/**
 * Cart Date/Time Picker for Kashkaval Catering
 * Compact version with modal interface
 */

class CartDateTimePicker {
  constructor() {
    this.config = {
      leadTimeHours: 6,
      businessHours: {
        open: { hour: 10, minute: 30 }, // 10:30 AM
        close: { hour: 21, minute: 30 }  // 9:30 PM
      },
      blackoutDates: [
        { month: 12, day: 24 }, // Christmas Eve
        { month: 12, day: 25 }, // Christmas Day
        { month: 7, day: 4 },   // July 4th
        // Thanksgiving: 4th Thursday of November (calculated dynamically)
      ],
      timezone: 'America/New_York'
    };

    this.selectedDate = null;
    this.selectedTime = null;

    // Modal elements
    this.modal = null;
    this.modalOverlay = null;
    this.dateInput = null;
    this.timeSelect = null;
    this.confirmBtn = null;
    this.iconBtn = null;
    this.summary = null;
    this.summaryText = null;

    this.init();
  }

  init() {
    this.cacheElements();
    this.setupEventListeners();
    this.populateTimeOptions();
    this.loadSavedSelection();
    this.updateUI();
  }

  cacheElements() {
    this.modal = document.getElementById('datetime-picker-modal');
    this.modalOverlay = this.modal?.querySelector('.datetime-modal__overlay');
    this.dateInput = document.getElementById('fulfillment-date');
    this.timeSelect = document.getElementById('fulfillment-time');
    this.confirmBtn = document.getElementById('datetime-confirm-btn');
    this.iconBtn = document.getElementById('datetime-icon-btn');
    this.summary = document.getElementById('datetime-summary');
    this.summaryText = this.summary?.querySelector('.datetime-summary__text');
    this.errorDiv = document.getElementById('datetime-modal-error');
  }

  setupEventListeners() {
    // Open modal when clicking icon button
    if (this.iconBtn) {
      this.iconBtn.addEventListener('click', () => this.openModal());
    }

    // Open modal when clicking anywhere on the summary
    if (this.summary) {
      this.summary.addEventListener('click', () => this.openModal());
      this.summary.style.cursor = 'pointer';
    }

    // Close modal
    const closeButtons = document.querySelectorAll('[data-datetime-modal-close]');
    closeButtons.forEach(btn => {
      btn.addEventListener('click', () => this.closeModal());
    });

    // Confirm button
    if (this.confirmBtn) {
      this.confirmBtn.addEventListener('click', () => this.handleConfirm());
    }

    // Date input
    if (this.dateInput) {
      // Set min date to earliest available (24 hours from now)
      const minDate = this.getEarliestAvailableDate();
      this.dateInput.min = this.formatDateForInput(minDate);

      // Set max date to 1 year from now
      const maxDate = new Date();
      maxDate.setFullYear(maxDate.getFullYear() + 1);
      this.dateInput.max = this.formatDateForInput(maxDate);

      this.dateInput.addEventListener('change', (e) => this.handleDateChange(e));
    }

    // Time select
    if (this.timeSelect) {
      this.timeSelect.addEventListener('change', () => this.clearError());
    }

    // Keyboard shortcuts
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal && this.modal.getAttribute('aria-hidden') === 'false') {
        this.closeModal();
      }
    });
  }

  populateTimeOptions() {
    if (!this.timeSelect) return;

    this.timeSelect.innerHTML = '<option value="">Select time</option>';

    // Generate times from 10:30 AM to 9:30 PM in 30-minute increments
    const times = [];
    let hour = 10;
    let minute = 30;

    while (hour < 21 || (hour === 21 && minute <= 30)) {
      let displayHour = hour;
      let period = 'AM';

      if (hour >= 12) {
        period = 'PM';
        if (hour > 12) displayHour = hour - 12;
      }

      const minuteStr = minute === 0 ? '00' : String(minute);
      const timeStr = `${displayHour}:${minuteStr} ${period}`;

      // Special case for noon
      const displayTime = (hour === 12 && minute === 0) ? 'Noon' : timeStr;

      times.push({ value: timeStr, display: displayTime });

      // Increment by 30 minutes
      minute += 30;
      if (minute >= 60) {
        minute = 0;
        hour++;
      }
    }

    times.forEach(time => {
      const option = document.createElement('option');
      option.value = time.value;
      option.textContent = time.display;
      this.timeSelect.appendChild(option);
    });
  }

  openModal() {
    if (!this.modal) return;

    // Pre-fill modal with existing values if any
    if (this.selectedDate && this.dateInput) {
      this.dateInput.value = this.selectedDate;
      this.filterTimeOptions();
    }
    if (this.selectedTime && this.timeSelect) {
      this.timeSelect.value = this.selectedTime;
    }

    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.clearError();

    // Focus on date input
    setTimeout(() => {
      if (this.dateInput) this.dateInput.focus();
    }, 100);
  }

  closeModal() {
    if (!this.modal) return;

    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.clearError();
  }

  async handleConfirm() {
    if (!this.dateInput || !this.timeSelect) return;

    const date = this.dateInput.value;
    const time = this.timeSelect.value;

    // Validate
    if (!date) {
      this.showError('Please select a date');
      return;
    }

    if (!time) {
      this.showError('Please select a time');
      return;
    }

    // Validate date is not blackout and meets lead time
    const selectedDate = new Date(date + 'T00:00:00');

    if (this.isBlackoutDate(selectedDate)) {
      this.showError('This date is not available. Please select another date.');
      return;
    }

    const earliestDateTime = this.getEarliestAvailableDate();
    const selectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    const earliestDateOnly = new Date(earliestDateTime.getFullYear(), earliestDateTime.getMonth(), earliestDateTime.getDate());

    if (selectedDateOnly < earliestDateOnly) {
      this.showError(`Please select a date at least 24 hours in advance. Earliest available: ${this.formatDateForDisplay(earliestDateTime)}`);
      return;
    }

    // Validate time meets 24-hour lead time
    const timeMatch = time.match(/(\d+):(\d+)\s+(AM|PM)/);
    if (timeMatch) {
      let hour = parseInt(timeMatch[1]);
      const minute = parseInt(timeMatch[2]);
      const period = timeMatch[3];

      // Convert to 24-hour
      if (period === 'PM' && hour !== 12) hour += 12;
      if (period === 'AM' && hour === 12) hour = 0;

      const selectedDateTime = new Date(date + 'T00:00:00');
      selectedDateTime.setHours(hour, minute, 0, 0);

      const now = new Date();
      const minDateTime = new Date(now.getTime() + (this.config.leadTimeHours * 60 * 60 * 1000));

      if (selectedDateTime < minDateTime) {
        this.showError('Please select a time at least 24 hours from now');
        return;
      }
    }

    // Save selection
    this.selectedDate = date;
    this.selectedTime = time;

    // Disable confirm button while saving
    if (this.confirmBtn) {
      this.confirmBtn.disabled = true;
      this.confirmBtn.textContent = 'Saving...';
    }

    try {
      await this.saveSelection();
      this.closeModal();
      this.updateUI();
    } catch (error) {
      this.showError('Error saving date/time. Please try again.');
      console.error('Error saving selection:', error);
    } finally {
      if (this.confirmBtn) {
        this.confirmBtn.disabled = false;
        this.confirmBtn.textContent = 'Confirm';
      }
    }
  }

  handleDateChange(e) {
    const selectedDate = new Date(e.target.value + 'T00:00:00');

    // Validate date is not blackout
    if (this.isBlackoutDate(selectedDate)) {
      this.showError('This date is not available. Please select another date.');
      e.target.value = '';
      return;
    }

    const earliestDateTime = this.getEarliestAvailableDate();
    const selectedDateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    const earliestDateOnly = new Date(earliestDateTime.getFullYear(), earliestDateTime.getMonth(), earliestDateTime.getDate());

    if (selectedDateOnly < earliestDateOnly) {
      this.showError(`Please select a date at least 24 hours in advance. Earliest available: ${this.formatDateForDisplay(earliestDateTime)}`);
      e.target.value = '';
      return;
    }

    this.filterTimeOptions();
    this.clearError();
  }

  filterTimeOptions() {
    if (!this.dateInput || !this.dateInput.value || !this.timeSelect) return;

    const selectedDate = new Date(this.dateInput.value + 'T00:00:00');
    const now = new Date();
    const earliest = new Date(now.getTime() + (this.config.leadTimeHours * 60 * 60 * 1000));

    // If selected date is same as earliest date, filter times
    const isSameDay = selectedDate.toDateString() === earliest.toDateString();

    if (isSameDay) {
      // Disable times before earliest time
      const earliestHour = earliest.getHours();
      const earliestMinute = earliest.getMinutes();

      Array.from(this.timeSelect.options).forEach(option => {
        if (option.value === '') return;

        const timeMatch = option.value.match(/(\d+):(\d+)\s+(AM|PM)/);
        if (timeMatch) {
          let hour = parseInt(timeMatch[1]);
          const minute = parseInt(timeMatch[2]);
          const period = timeMatch[3];

          // Convert to 24-hour
          if (period === 'PM' && hour !== 12) hour += 12;
          if (period === 'AM' && hour === 12) hour = 0;

          // Check if this time is before earliest allowed time
          if (hour < earliestHour || (hour === earliestHour && minute < earliestMinute)) {
            option.disabled = true;
            option.style.color = '#ccc';
          } else {
            option.disabled = false;
            option.style.color = '';
          }
        }
      });
    } else {
      // Enable all times
      Array.from(this.timeSelect.options).forEach(option => {
        option.disabled = false;
        option.style.color = '';
      });
    }
  }

  async saveSelection() {
    if (!this.selectedDate || !this.selectedTime) return;

    const dateObj = new Date(this.selectedDate + 'T00:00:00');
    const formattedDate = this.formatDateForDisplay(dateObj);
    const displayText = `${formattedDate} at ${this.selectedTime}`;

    // Save to cart attributes (via queue to avoid 429 rate limiting)
    const body = JSON.stringify({
      attributes: {
        'Fulfillment Date': this.selectedDate,
        'Fulfillment Time': this.selectedTime,
        'Pickup/Delivery Date & Time': displayText
      },
      note: `Pickup/Delivery: ${displayText}`
    });

    const makeRequest = async () => {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body
      });
      if (!response.ok) throw new Error('Failed to update cart');
      return response.json();
    };

    if (window.cartManagerInstance) {
      return window.cartManagerInstance.queueRequest(makeRequest);
    }
    return makeRequest();
  }

  async loadSavedSelection() {
    try {
      // Use CartManager to get cart data (avoids rate limiting)
      if (!window.cartManagerInstance) {
        console.warn('CartManager not initialized yet');
        return;
      }

      const cart = await window.cartManagerInstance.getCart();

      // If cart is empty, clear the date/time attributes
      if (!cart.items || cart.items.length === 0) {
        this.clearSelection();
        return;
      }

      if (cart.attributes) {
        const savedDate = cart.attributes['Fulfillment Date'];
        const savedTime = cart.attributes['Fulfillment Time'];

        if (savedDate && savedTime) {
          this.selectedDate = savedDate;
          this.selectedTime = savedTime;
        }
      }

      this.updateUI();
    } catch (error) {
      console.error('Error loading cart:', error);
    }
  }

  clearSelection() {
    this.selectedDate = null;
    this.selectedTime = null;

    if (this.dateInput) this.dateInput.value = '';
    if (this.timeSelect) this.timeSelect.value = '';

    // Clear cart attributes (via queue to avoid 429 rate limiting)
    const clearBody = JSON.stringify({
      attributes: {
        'Fulfillment Date': '',
        'Fulfillment Time': '',
        'Pickup/Delivery Date & Time': ''
      },
      note: ''
    });

    const clearRequest = () => fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: clearBody
    }).catch(error => {
      console.error('Error clearing cart attributes:', error);
    });

    if (window.cartManagerInstance) {
      window.cartManagerInstance.queueRequest(clearRequest).catch(error => {
        console.error('Error clearing cart attributes:', error);
      });
    } else {
      clearRequest();
    }

    this.updateUI();
  }

  updateUI() {
    if (!this.summary || !this.summaryText) return;

    if (this.selectedDate && this.selectedTime) {
      // Success state - date selected
      const dateObj = new Date(this.selectedDate + 'T00:00:00');
      const formattedDate = this.formatDateForDisplay(dateObj);

      this.summaryText.textContent = `${formattedDate} at ${this.selectedTime}`;
      this.summary.classList.remove('cart-datetime-picker__summary--warning');
      this.summary.classList.add('cart-datetime-picker__summary--success');
    } else {
      // Warning state - no date selected
      this.summaryText.textContent = 'Please select order date and time.';
      this.summary.classList.remove('cart-datetime-picker__summary--success');
      this.summary.classList.add('cart-datetime-picker__summary--warning');
    }

    this.updateCheckoutButton();
  }

  updateCheckoutButton() {
    const checkoutBtn = document.getElementById('checkout-button');
    if (!checkoutBtn) return;

    const hasValidSelection = this.selectedDate && this.selectedTime;

    if (!hasValidSelection) {
      checkoutBtn.style.opacity = '0.5';
      checkoutBtn.style.pointerEvents = 'none';
      checkoutBtn.style.cursor = 'not-allowed';
      checkoutBtn.setAttribute('aria-disabled', 'true');
    } else {
      checkoutBtn.style.opacity = '1';
      checkoutBtn.style.pointerEvents = 'auto';
      checkoutBtn.style.cursor = 'pointer';
      checkoutBtn.removeAttribute('aria-disabled');
    }
  }

  showError(message) {
    if (!this.errorDiv) return;
    this.errorDiv.textContent = message;
    this.errorDiv.classList.add('datetime-modal__error--visible');
  }

  clearError() {
    if (!this.errorDiv) return;
    this.errorDiv.textContent = '';
    this.errorDiv.classList.remove('datetime-modal__error--visible');
  }

  getEarliestAvailableDate() {
    const now = new Date();
    const earliest = new Date(now.getTime() + (this.config.leadTimeHours * 60 * 60 * 1000));

    // If earliest time is after business close, move to next day at open
    const closeTime = new Date(earliest);
    closeTime.setHours(this.config.businessHours.close.hour, this.config.businessHours.close.minute, 0, 0);

    if (earliest > closeTime) {
      earliest.setDate(earliest.getDate() + 1);
      earliest.setHours(this.config.businessHours.open.hour, this.config.businessHours.open.minute, 0, 0);
    }

    // Skip blackout dates
    while (this.isBlackoutDate(earliest)) {
      earliest.setDate(earliest.getDate() + 1);
    }

    return earliest;
  }

  isBlackoutDate(date) {
    const month = date.getMonth() + 1;
    const day = date.getDate();

    // Check fixed blackout dates
    for (const blackout of this.config.blackoutDates) {
      if (blackout.month === month && blackout.day === day) {
        return true;
      }
    }

    // Check Thanksgiving (4th Thursday of November)
    if (month === 11) {
      const thanksgiving = this.getThanksgivingDate(date.getFullYear());
      if (thanksgiving && day === thanksgiving.getDate()) {
        return true;
      }
    }

    return false;
  }

  getThanksgivingDate(year) {
    const november = new Date(year, 10, 1);
    let thursdayCount = 0;

    for (let day = 1; day <= 30; day++) {
      const date = new Date(year, 10, day);
      if (date.getDay() === 4) {
        thursdayCount++;
        if (thursdayCount === 4) {
          return date;
        }
      }
    }
    return null;
  }

  formatDateForInput(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatDateForDisplay(date) {
    const options = { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartDateTimePicker = new CartDateTimePicker();
  });
} else {
  window.cartDateTimePicker = new CartDateTimePicker();
}

// Re-initialize when cart drawer opens
document.addEventListener('cart:updated', () => {
  setTimeout(() => {
    if (window.cartDateTimePicker) {
      window.cartDateTimePicker.loadSavedSelection();
    } else {
      window.cartDateTimePicker = new CartDateTimePicker();
    }
    // Also re-initialize supplies
    if (window.cartSuppliesManager) {
      window.cartSuppliesManager.loadSavedSelections();
    } else {
      window.cartSuppliesManager = new CartSuppliesManager();
    }
  }, 100);
});

/**
 * Cart Supplies Manager
 * Handles event supplies checkboxes (utensils, napkins, plates, serving utensils)
 */
class CartSuppliesManager {
  constructor() {
    this.checkboxes = {
      utensils: document.getElementById('supplies-utensils'),
      napkins: document.getElementById('supplies-napkins'),
      plates: document.getElementById('supplies-plates'),
      servingUtensils: document.getElementById('supplies-serving-utensils')
    };

    this.init();
  }

  init() {
    this.setupEventListeners();
    this.loadSavedSelections();
  }

  setupEventListeners() {
    // Listen to checkbox changes
    Object.values(this.checkboxes).forEach(checkbox => {
      if (checkbox) {
        checkbox.addEventListener('change', () => this.saveSelections());
      }
    });
  }

  async saveSelections() {
    const selections = {
      'Supplies - Utensils': this.checkboxes.utensils?.checked ? 'Yes' : 'No',
      'Supplies - Napkins': this.checkboxes.napkins?.checked ? 'Yes' : 'No',
      'Supplies - Plates': this.checkboxes.plates?.checked ? 'Yes' : 'No',
      'Supplies - Serving Utensils': this.checkboxes.servingUtensils?.checked ? 'Yes' : 'No'
    };

    const body = JSON.stringify({ attributes: selections });

    const makeRequest = async () => {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body
      });
      if (!response.ok) throw new Error('Failed to update cart');
      return response.json();
    };

    try {
      if (window.cartManagerInstance) {
        return await window.cartManagerInstance.queueRequest(makeRequest);
      }
      return await makeRequest();
    } catch (error) {
      console.error('Error saving supplies selections:', error);
    }
  }

  async loadSavedSelections() {
    try {
      // Use CartManager to get cart data (avoids rate limiting)
      if (!window.cartManagerInstance) {
        console.warn('CartManager not initialized yet');
        return;
      }

      const cart = await window.cartManagerInstance.getCart();

      if (cart.attributes) {
        // Load saved selections
        if (this.checkboxes.utensils) {
          this.checkboxes.utensils.checked = cart.attributes['Supplies - Utensils'] === 'Yes';
        }
        if (this.checkboxes.napkins) {
          this.checkboxes.napkins.checked = cart.attributes['Supplies - Napkins'] === 'Yes';
        }
        if (this.checkboxes.plates) {
          this.checkboxes.plates.checked = cart.attributes['Supplies - Plates'] === 'Yes';
        }
        if (this.checkboxes.servingUtensils) {
          this.checkboxes.servingUtensils.checked = cart.attributes['Supplies - Serving Utensils'] === 'Yes';
        }
      }

      // If cart is empty, clear all selections
      if (!cart.items || cart.items.length === 0) {
        this.clearSelections();
      }
    } catch (error) {
      console.error('Error loading supplies selections:', error);
    }
  }

  clearSelections() {
    Object.values(this.checkboxes).forEach(checkbox => {
      if (checkbox) {
        checkbox.checked = false;
      }
    });

    // Clear cart attributes (via queue to avoid 429 rate limiting)
    const clearBody = JSON.stringify({
      attributes: {
        'Supplies - Utensils': '',
        'Supplies - Napkins': '',
        'Supplies - Plates': '',
        'Supplies - Serving Utensils': ''
      }
    });

    const clearRequest = () => fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: clearBody
    }).catch(error => {
      console.error('Error clearing supplies selections:', error);
    });

    if (window.cartManagerInstance) {
      window.cartManagerInstance.queueRequest(clearRequest).catch(error => {
        console.error('Error clearing supplies selections:', error);
      });
    } else {
      clearRequest();
    }
  }
}

// Initialize supplies manager when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.cartSuppliesManager = new CartSuppliesManager();
  });
} else {
  window.cartSuppliesManager = new CartSuppliesManager();
}
